import * as React from 'react'
import { useTranslation } from 'react-i18next'
import Logo from './Logo'
import LanguageSwitcher from './LanguageSwitcher'
import { SHELL_NS } from '../i18n'
import { LOGIN_PATH } from '../auth/privateAccess'
import type { StartupFailure, StartupBlock } from '../auth/AuthProvider'

/** Everything this page can be showing: three faults, and one closed door. */
export type StartupReason = StartupFailure | StartupBlock

/**
 * The support-facing half of each *failure*: a stable code, never translated,
 * because it is the one string that has to survive being read down a phone line and
 * pasted into a log search.
 *
 * The visitor-facing half lives in the shell catalogs under
 * `startupError.reason.<reason>` — the copy is specific per reason because "we
 * could not tell who you are" and "we could not load the library's settings" are
 * different enough that a single generic apology would read as a shrug.
 *
 * Tone, in every locale: light, never cute about the visitor's time, and honest that
 * the fault is ours.
 *
 * `private` has no entry on purpose: nothing failed, so there is nothing for support
 * to look up. See {@link StartupError}.
 */
const CODES: Record<StartupFailure, string> = {
  whoami: 'AUTH_UNREACHABLE',
  configuration: 'CONFIG_UNREACHABLE',
  unknown: 'STARTUP_FAILED',
}

/**
 * The startup gate's unhappy path: shown, full-viewport, when the handshake settled
 * badly (see `AuthState.failed`). The app is never mounted behind it — that is the
 * point. Half an app that cannot tell who you are or attach a CSRF token is worse
 * than an honest stop, because it fails later, further from the cause.
 *
 * It carries the two pieces of chrome that are shell-owned rather than app state —
 * the mark and the language switch — and nothing else: no nav, no pilets, no router
 * links. A visitor who lands here has not been shown the app yet, so this page is
 * the whole of medialib to them; an unbranded apology in the wrong language would be
 * indistinguishable from a hosting provider's error page. Both are safe here because
 * both are bundled into the shell's own entry: the language catalogs are static (see
 * `shellResources`) and the mark is inline SVG, so neither needs the network that
 * just failed.
 *
 * For a failure the only way *out* is a reload, because the failure is a request
 * that did not come back and nothing in-app can retry it — the shell's startup
 * requests are single shared promises for the life of the page, so a fresh page is
 * genuinely the retry. Switching language, by contrast, re-renders in place: it
 * changes nothing about the failure, it just lets the visitor read the explanation.
 *
 * **`private` is not a failure**, and the page drops its whole apparatus of one:
 * no hint that it is probably our fault, no reload, no support code. A members-only
 * library working exactly as configured has nothing to apologise for and nothing for
 * support to look up, and offering a reload would be a lie — reloading produces the
 * same anonymous visitor every time. Signing in is the only thing that changes the
 * answer, so it is the only control on the page. It is also announced politely
 * (`role='status'`) rather than as an alert.
 */
const StartupError: React.FC<{ reason: StartupReason }> = ({ reason }) => {
  const { t } = useTranslation(SHELL_NS)
  // Everything below keys off this one distinction rather than off each reason.
  const isFault = reason !== 'private'
  const code = isFault ? CODES[reason] ?? CODES.unknown : null

  return (
    <div className='StartupError' role={isFault ? 'alert' : 'status'}>
      {/* Not the app's <Header>: that one is a fixed, full-bleed bar carrying the
          nav and the pilet header actions, none of which exist here. This is the
          same two controls at the same inset, laid out for a page that has nothing
          else on it. */}
      <div className='StartupError__chrome'>
        <Logo linked={false} />
        <LanguageSwitcher />
      </div>
      <div className='StartupError__content'>
        <p className='StartupError__glyph' aria-hidden='true'>🍿</p>
        <h1 className='StartupError__title'>{t('startupError.title')}</h1>
        <p className='StartupError__message'>{t(`startupError.reason.${reason}`, { defaultValue: t('startupError.reason.unknown') })}</p>
        {isFault && <p className='StartupError__hint'>{t('startupError.hint')}</p>}
        <div className='StartupError__actions'>
          {isFault && (
            <button
              type='button'
              className='StartupError__retry'
              onClick={() => window.location.reload()}
              autoFocus
            >
              {t('startupError.retry')}
            </button>
          )}
          {/* On `private` this is the page's only control, and so the primary one.
              On the `whoami` failure it is offered as a secondary choice: that call
              is the one that decides who the visitor is, and among the ways it can
              answer badly is a session the backend will no longer accept — which no
              amount of reloading fixes, because the shell would keep sending the
              same cookie. Signing in again is the one action available to the
              visitor that changes the input. It stays second there because the
              other, likelier readings of a non-200/403 (the endpoint is down, the
              request never left) are ours to fix, not theirs.

              A plain <a>, not a router <Link>: /login is a separate external app, so
              it needs a full-page navigation — the same reason the profile pilet's
              header action uses one. Doubly so here, where the router is the thing
              we are refusing to mount. */}
          {(reason === 'private' || reason === 'whoami') && (
            <a
              className='StartupError__login'
              href={LOGIN_PATH}
              data-primary={!isFault}
              autoFocus={!isFault}
            >
              {t('startupError.login')}
            </a>
          )}
        </div>
        {code && <p className='StartupError__code'>{code}</p>}
      </div>
    </div>
  )
}

export default StartupError
